import {
  Badge,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api.js";

interface AuditEvent {
  id: string;
  actorType: string;
  actorId: string;
  action: string;
  resourceType: string;
  resourceId: string;
  workspaceId: string | null;
  environment: string | null;
  createdAt: string;
}

const actorColor = (type: string) => {
  if (type === "internal_user") return "dusk";
  if (type === "workspace_user") return "brand";
  return "gray";
};

export const AuditPage = () => {
  const [actorFilter, setActorFilter] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["internal", "audit", actorFilter],
    queryFn: () =>
      api.get<{ events: AuditEvent[] }>(
        `/internal/audit${actorFilter ? `?actorType=${actorFilter}` : ""}`
      )
  });

  const events = (data?.events ?? []).filter(
    (e) =>
      !search ||
      e.action.includes(search) ||
      e.actorId.includes(search) ||
      (e.workspaceId ?? "").includes(search)
  );

  return (
    <Stack gap="md">
      <Title order={2}>Audit log</Title>

      <Group>
        <TextInput
          placeholder="Search actions, actors..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: 1, maxWidth: 360 }}
        />
        <Select
          placeholder="Actor type"
          data={[
            { value: "internal_user", label: "Internal user" },
            { value: "workspace_user", label: "Workspace user" },
            { value: "agent", label: "Agent" }
          ]}
          value={actorFilter}
          onChange={setActorFilter}
          clearable
          style={{ width: 180 }}
        />
      </Group>

      <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
        {isLoading ? (
          <Group justify="center" p="xl">
            <Loader size="sm" />
          </Group>
        ) : (
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Time</Table.Th>
                <Table.Th>Actor</Table.Th>
                <Table.Th>Action</Table.Th>
                <Table.Th>Resource</Table.Th>
                <Table.Th>Workspace</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {events.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c="dimmed" size="sm" ta="center" p="md">
                      No audit events
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                events.map((e) => (
                  <Table.Tr key={e.id}>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {new Date(e.createdAt).toLocaleString()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" color={actorColor(e.actorType)} variant="light">
                        {e.actorType}
                      </Badge>
                      <Text size="xs" mt={2}>
                        {e.actorId}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" ff="monospace">
                        {e.action}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {e.resourceType}/{e.resourceId}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {e.workspaceId ?? "—"}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
};
